import { useEffect, useState, useMemo, useContext, useRef } from "react";
import { useNavigate } from "react-router";

import { TabView, TabPanel } from "primereact/tabview";
import { InputText } from "primereact/inputtext";
import { ScrollPanel } from "primereact/scrollpanel";
import { DataView } from "primereact/dataview";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { ContentCardSkeleton } from "sj-ui-toolkit/src/atlas/components/ContentCard";
import { PathCard } from "sj-ui-toolkit/src/atlas/components/ContentCard/PathCard/PathCard";
import { getColorForLevel } from "sj-ui-toolkit/src/atlas/utils/svgClasses";
import type { VariantType } from "sj-ui-toolkit/src/atlas/components/ContentCard/contentCardTypes";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";

import { PathsContext } from "@/context/PathsContext";
import {
  loadFilteredPathsAsync,
  getPathType,
  getActivityPathConflict,
  toggleActivated,
  type PathData,
} from "@/api/paths";
import { loadLevelsAsync } from "@/api/levels";
import ClonePathModal from "@/components/paths/ClonePathModal";
import { truncate } from "lodash";

const NO_PATHS_FOUND = "No paths found";

interface LevelOption {
  label: string;
  value: string; // rankName
}

interface PathsState {
  customPaths: PathData[];
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const PathsCustom = () => {
  useBreadcrumb({
    label: "Custom Paths",
    route: AdminAppRoutes.routes.paths.routes.custom.route,
    display: "none",
    resetTrail: true,
  });

  const navigate = useNavigate();
  const { showToast } = useToast();
  const { allPathNames, allPaths, loadPathsAsync, updatePathActivation } = useContext(PathsContext);

  const [data, setData] = useState<PathsState>({ customPaths: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [levels, setLevels] = useState<LevelOption[]>([]);
  const [levelsLoading, setLevelsLoading] = useState(true);
  const [isLoadingToggle, setIsLoadingToggle] = useState(false);
  const [isCloneModalVisible, setIsCloneModalVisible] = useState(false);
  const [selectedClonePath, setSelectedClonePath] = useState<PathData | null>(null);

  const isMountedRef = useRef(false);

  useEffect(() => {
    if (isMountedRef.current) return;
    isMountedRef.current = true;
    void fetchData();
    void fetchLevels();
    void loadPathsAsync();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await loadFilteredPathsAsync({ path_type: "path", path_source: "custom" });
      setData({ customPaths: response.paths ?? [] });
    } catch {
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Failed to load custom paths",
        life: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLevels = async () => {
    setLevelsLoading(true);
    try {
      const result = await loadLevelsAsync();
      const levelOptions = result.courses
        .filter(level => level.name !== "Security Awareness")
        .map(level => ({ label: level.name, value: level.rankName }));
      setLevels(levelOptions);
    } catch (error) {
      console.error("Error loading levels:", error);
    } finally {
      setLevelsLoading(false);
    }
  };

  const filterData = (items: PathData[]) => {
    return items.filter(item => {
      const matchesSearch =
        !searchTerm ||
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesLevel =
        selectedLevels.length === 0 ||
        (item.rankName != null && selectedLevels.includes(item.rankName));
      return matchesSearch && matchesLevel;
    });
  };

  const handleToggleActivated = async (item: PathData) => {
    const isActivating = !item.activated;

    if (isActivating) {
      const conflict = getActivityPathConflict(item.rankName, allPaths);
      if (conflict) {
        showToast({
          severity: "warn",
          summary: "Active path conflict",
          detail: `"${conflict.name}" is the currently active ${item.levelName ?? item.rankName} path. Deactivate it before activating "${item.name}".`,
          life: 8000,
        });
        return;
      }
    }

    setIsLoadingToggle(true);
    await toggleActivated({
      id: item.id!,
      paths: data.customPaths,
      type: "my_paths",
      setData: setData as React.Dispatch<
        React.SetStateAction<{
          customPaths?: PathData[];
          defaultPaths?: PathData[];
          quests?: PathData[];
        }>
      >,
      onSuccess: () => updatePathActivation(item.id!, isActivating),
      onRevert: () => updatePathActivation(item.id!, !isActivating),
      showToast,
    });
    setIsLoadingToggle(false);
  };

  const filteredData = useMemo(
    () => ({
      active: filterData(data.customPaths.filter(item => item.activated && !item.archived)),
      inactive: filterData(data.customPaths.filter(item => !(item.activated && !item.archived))),
    }),
    [data, searchTerm, selectedLevels]
  );

  return (
    <div className="relative w-full">
      <div className="mb-4 flex items-center justify-between">
        <h1>Custom Paths</h1>
        <Button
          label="Create New Path"
          icon="pi pi-graduation-cap"
          data-testid="custom-paths-create-button"
          onClick={() => navigate(`${AdminAppRoutes.routes.paths.routes.new.route}?type=my_paths`)}
        />
      </div>

      <div className="py-8">
        <div className="flex flex-wrap gap-4 items-center mb-4">
          <InputText
            value={searchTerm}
            disabled={isLoading}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search Paths"
            aria-label="Search paths"
            data-testid="custom-paths-search-input"
          />
          <MultiSelect
            value={selectedLevels}
            options={levels}
            disabled={isLoading || levelsLoading}
            onChange={e => setSelectedLevels(e.value as string[])}
            optionLabel="label"
            placeholder="Select Levels"
            display="chip"
            showClear
            maxSelectedLabels={3}
            className="w-80"
            aria-label="Filter by level"
          />
        </div>
      </div>

      <TabView
        aria-label="Custom paths"
        activeIndex={activeTabIndex}
        onTabChange={e => setActiveTabIndex(e.index)}
      >
        <TabPanel header="Active">
          <ScrollPanel
            style={{ width: "100%", height: "70vh" }}
            pt={{ barY: { style: { backgroundColor: "var(--primary-color)" } } }}
          >
            <CatalogTabContent
              data={filteredData.active}
              isLoading={isLoading}
              toggleActivated={handleToggleActivated}
              isLoadingToggle={isLoadingToggle}
              onClone={path => {
                setSelectedClonePath(path);
                setIsCloneModalVisible(true);
              }}
            />
          </ScrollPanel>
        </TabPanel>
        <TabPanel header="Inactive">
          <ScrollPanel
            style={{ width: "100%", height: "70vh" }}
            pt={{ barY: { style: { backgroundColor: "var(--primary-color)" } } }}
          >
            <CatalogTabContent
              data={filteredData.inactive}
              isLoading={isLoading}
              toggleActivated={handleToggleActivated}
              isLoadingToggle={isLoadingToggle}
              onClone={path => {
                setSelectedClonePath(path);
                setIsCloneModalVisible(true);
              }}
            />
          </ScrollPanel>
        </TabPanel>
      </TabView>

      <ClonePathModal
        visible={isCloneModalVisible}
        onHide={() => setIsCloneModalVisible(false)}
        originalPath={selectedClonePath}
        existingPathNames={allPathNames}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab content sub-component
// ---------------------------------------------------------------------------

interface CatalogTabContentProps {
  data: PathData[];
  isLoading: boolean;
  toggleActivated: (item: PathData) => void;
  isLoadingToggle: boolean;
  onClone: (path: PathData) => void;
}

const CatalogTabContent = ({
  data,
  isLoading,
  toggleActivated,
  isLoadingToggle,
  onClone,
}: CatalogTabContentProps) => {
  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="mb-4">
          <Skeleton width="10rem" height="1rem" />
        </div>
        <div className="flex flex-wrap gap-6">
          {Array(8)
            .fill(null)
            .map((_, idx) => (
              <ContentCardSkeleton key={idx} cardSize="small" showActivated />
            ))}
        </div>
      </div>
    );
  }

  const getRolesForPath = (path: PathData) => {
    const allAudiences: string[] = [];
    path.audiences?.forEach(a => allAudiences.push(a.value));
    return allAudiences;
  };

  const listTemplate = (items: PathData[]) => {
    const groupedItems: Record<string, PathData[]> = {};

    items.forEach(item => {
      const audiences = getRolesForPath(item);
      const primaryAudience = audiences.length > 0 ? [...audiences].sort()[0]! : "";
      if (!groupedItems[primaryAudience]) groupedItems[primaryAudience] = [];
      groupedItems[primaryAudience]!.push(item);
    });

    const sortedAudiences = Object.keys(groupedItems).sort((a, b) => {
      if (a === "") return 1;
      if (b === "") return -1;
      return a.localeCompare(b);
    });

    sortedAudiences.forEach(audience => {
      groupedItems[audience]!.sort(
        (a, b) => ((a.levelId as number) ?? 999) - ((b.levelId as number) ?? 999)
      );
    });

    return (
      <div className="space-y-8">
        {sortedAudiences.map(audience => (
          <div key={audience}>
            <h3 className="atlas-h5 mb-4">{audience || "Other"}</h3>
            <div className="flex flex-wrap gap-6">
              {groupedItems[audience]!.map(item => (
                <PathCard
                  key={item.id}
                  contentType="path"
                  lessonType={undefined}
                  pathType={getPathType(item)}
                  id={String(item.id ?? "")}
                  name={truncate(item.name, { length: 50 })}
                  description={truncate(item.description, { length: 120 })}
                  lessonsInEnrollment={item.lessonsCount}
                  estimatedTimeToComplete={item.totalDuration}
                  variant={getColorForLevel(item.rankName ?? "") as VariantType}
                  cardSize="small"
                  showActivated
                  activated={item.activated}
                  onActivate={() => toggleActivated(item)}
                  isLoadingToggle={isLoadingToggle}
                  onClone={() => onClone(item)}
                  restricted={item.restricted}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <DataView
      value={data}
      listTemplate={listTemplate}
      layout="list"
      emptyMessage={NO_PATHS_FOUND}
    />
  );
};

export default PathsCustom;
